/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { View } from "react-native";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import Icon from "react-icons-kit";
import { clockO } from "react-icons-kit/fa/clockO";
import { closeRound } from "react-icons-kit/ionicons/closeRound";
import { ic_file_download } from "react-icons-kit/md/ic_file_download";
import { ic_calendar_today_twotone } from "react-icons-kit/md/ic_calendar_today_twotone";

import { LoadingView, Text, TranscriptionBlock } from "UpduoLibrary";
import {
  skaleWhite,
  skaleGray,
  skaleBackgroundGray,
  skaleBlue,
  upduoFailure,
} from "config/UpduoColors";
import {
  fmtMSS,
  formattedDateAndTimeString,
  getTranscriptJson,
} from "config/Utilities";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import { UserType } from "graphql/internal/generated";
import { RootState } from "store/types";
import { ic_perm_identity_outline } from "react-icons-kit/md/ic_perm_identity_outline";

export default function TranscriptionSidePane({}) {
  const dispatch = useDispatch();
  let runningTime = 0;
  const transcriptionObject = useSelector(
    (state: RootState) => state.transcriptionObject
  );
  const [transcriptJson, setTranscriptJson] = useState<any>();
  const [loadTranscriptError, setLoadTranscriptError] = useState<any>();

  const getAndSetTranscriptJson = async () => {
    setTranscriptJson(null);
    setLoadTranscriptError(null);
    try {
      if (transcriptionObject.transcriptionUrl) {
        const transcriptJson = await getTranscriptJson(transcriptionObject);
        setTranscriptJson(transcriptJson);
      }
    } catch (err: any) {
      UpduoAnalytics.logEvent(
        "app-dashboard-activity-gettranscriptjson-error",
        {
          error: err,
        }
      );
      console.error(err);
      setLoadTranscriptError(`Error loading transcript: "${err?.message}"`);
    }
  };

  useEffect(() => {
    if (transcriptionObject) {
      getAndSetTranscriptJson();
    }
  }, [transcriptionObject]);

  const generateParticipantsRow = (
    user: UserType,
    role: "teacher" | "learner"
  ) => (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        zIndex: role === "teacher" ? 10 : 0,
      }}
    >
      <View
        style={{
          borderRadius: 8,
          backgroundColor: skaleBackgroundGray,
          flexDirection: "row",
          padding: 10.5,
          alignItems: "center",
          marginRight: 8,
          maxHeight: 40,
        }}
      >
        <Icon
          style={{ color: "#347FF6", marginRight: 8 }}
          size={20}
          icon={ic_perm_identity_outline}
        />
        <Text>{`${user.firstName} ${user.lastName}`}</Text>
      </View>
    </View>
  );

  return (
    <View
      style={{
        backgroundColor: skaleWhite,
        padding: "20px",
        borderColor: "#E3E3E3",
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderStyle: "solid",
        maxHeight: window.innerHeight,
      }}
    >
      <View
        style={{
          maxHeight: 259,
          borderBottomColor: "#E3E3E3",
          borderBottomWidth: 1,
          marginBottom: 28,
          zIndex: 1,
        }}
      >
        <View style={{ justifyContent: "flex-end", flexDirection: "row" }}>
          <Icon
            style={{ color: skaleGray, cursor: "pointer" }}
            size={11}
            icon={closeRound}
            onClick={() =>
              dispatch(ActionCreators.setTranscriptionObject(null))
            }
          />
        </View>

        <Text style={{ fontWeight: "700", fontSize: 20 }}>
          {transcriptionObject?.skill?.name}
        </Text>

        <View style={{ flexDirection: "row", marginTop: 16 }}>
          <View
            style={{
              backgroundColor: skaleBackgroundGray,
              borderRadius: 8,
              padding: 11,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Icon
              style={{ color: "#B0B7C1" }}
              size={20}
              icon={ic_calendar_today_twotone}
            />
            <Text style={{ fontSize: 14, marginLeft: 10 }}>
              {formattedDateAndTimeString(
                new Date(transcriptionObject.created),
                false
              )}
            </Text>
          </View>

          <View
            style={{
              marginLeft: 8,
              backgroundColor: skaleBackgroundGray,
              borderRadius: 8,
              padding: 11,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Icon style={{ color: "#B0B7C1" }} size={23} icon={clockO} />
            <Text style={{ fontSize: 14, marginLeft: 8 }}>
              {fmtMSS(transcriptionObject.duration)}
            </Text>
          </View>
          {transcriptionObject.transcriptionUrl && (
            <a
              style={{
                marginLeft: 8,
                backgroundColor: skaleBackgroundGray,
                borderRadius: 8,
                padding: 11,
                display: "flex",
                flexDirection: "row",
                cursor: "pointer",
                textDecoration: "none",
              }}
              href={transcriptionObject.transcriptionUrl}
              download
            >
              <Icon
                style={{ color: skaleBlue }}
                size={23}
                icon={ic_file_download}
              />
              <Text style={{ fontSize: 14, marginLeft: 8 }}>
                {"Download Transcription"}
              </Text>
            </a>
          )}
        </View>

        <Text
          style={{
            fontSize: 18,
            fontWeight: "500",
            marginTop: 20,
          }}
        >
          {"Participants:"}
        </Text>
        <View style={{ flexDirection: "row", marginTop: 16, marginBottom: 26 }}>
          {transcriptionObject.teacher &&
            generateParticipantsRow(transcriptionObject.teacher, "teacher")}
          {transcriptionObject.learner &&
            generateParticipantsRow(transcriptionObject.learner, "learner")}
        </View>
      </View>

      {loadTranscriptError ? (
        <Text style={{ color: upduoFailure, textAlign: "center" }}>
          {loadTranscriptError}
        </Text>
      ) : transcriptJson ? (
        <View
          style={{
            overflow: "scroll",
            width: "100%",
            // TODO this doesn't update with window resize (also probably not the best way to do this)
            // maybe we could set the parent to max-height: calc(100vh - margin px) and then just set
            // this to grow with a reasonable flex-basis
            maxHeight: window.innerHeight - 400,
            alignItems: "flex-start",
          }}
        >
          {transcriptJson.map((block) => {
            runningTime += block.ts;
            return (
              <TranscriptionBlock
                name={`Speaker ${block.speaker + 1}`}
                text={block.text}
                key={runningTime}
                timestamp={Math.floor(runningTime)}
              />
            );
          })}
        </View>
      ) : (
        <LoadingView bottomSpacing={50} />
      )}
    </View>
  );
}
